/**
 * Parent Verification Page
 * Landing page when parent clicks verification link from email.
 * Shows teen info, allows parent to create their account.
 */

import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Leaf, Check, Loader2, AlertCircle, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ParentVerificationPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [verificationData, setVerificationData] = useState(null);
  
  // Form state
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  
  // Fetch verification status
  useEffect(() => {
    const fetchStatus = async () => {
      if (!token) {
        setError("Invalid verification link");
        setLoading(false);
        return;
      }
      
      try {
        const res = await axios.get(`${API_URL}/api/parent/verify-status/${token}`);
        setVerificationData(res.data);
        setEmail(res.data.parent_email);
      } catch (err) {
        setError(err.response?.data?.detail || "Invalid or expired verification link");
      } finally {
        setLoading(false);
      }
    };
    
    fetchStatus();
  }, [token]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error("Passwords don't match");
      return;
    }
    
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setSubmitting(true);
    
    try {
      const res = await axios.post(`${API_URL}/api/parent/verify-and-register`, {
        token,
        name,
        email,
        password
      });
      
      // Store token and redirect
      localStorage.setItem("reclaim_token", res.data.token);
      toast.success("Welcome to Reclaim!");
      
      // Navigate to parent challenge
      navigate("/parent/challenge");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally {
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#F5F2ED] to-[#E6E2DD] flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        >
          <Leaf className="w-12 h-12 text-[#4A6741]" />
        </motion.div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#F5F2ED] to-[#E6E2DD] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center"
        >
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="text-xl font-semibold text-[#292524] mb-2">Verification Error</h2>
          <p className="text-[#78716C] mb-6">{error}</p>
          <Button onClick={() => navigate("/")} variant="outline">
            Go to Home
          </Button>
        </motion.div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F5F2ED] to-[#E6E2DD]">
      <div className="container mx-auto px-4 py-8 max-w-lg">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="inline-flex items-center gap-2 bg-[#4A6741]/10 px-4 py-2 rounded-full mb-4">
            <Leaf className="w-4 h-4 text-[#4A6741]" />
            <span className="text-sm font-medium text-[#4A6741]">Reclaim</span>
          </div>
          <h1 className="text-2xl font-bold text-[#292524] mb-2">
            Welcome, Parent
          </h1>
          <p className="text-[#78716C]">
            Verify your teen's account and begin your own journey
          </p>
        </motion.div>
        
        {/* Teen Info Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-2xl shadow-lg p-6 mb-6"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-full bg-[#4A6741]/10 flex items-center justify-center flex-shrink-0">
              <Heart className="w-6 h-6 text-[#4A6741]" />
            </div>
            <div>
              <h3 className="font-semibold text-[#292524] mb-1">
                Teen Zone Access for {verificationData?.teen_name}
              </h3>
              <p className="text-sm text-[#78716C]">
                Your teen has joined Reclaim's Teen Zone — a safe, supportive space 
                designed to help young people build confidence and emotional resilience.
              </p>
            </div>
          </div>
          
          <div className="mt-4 pt-4 border-t border-[#E6E2DD]">
            <div className="flex items-center gap-2 text-sm text-[#4A6741]">
              <Check className="w-4 h-4" />
              <span>By creating your account, you verify your consent</span>
            </div>
          </div>
        </motion.div>
        
        {/* Parent Program Intro */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-br from-[#D4A373]/20 to-[#D4A373]/10 rounded-2xl p-6 mb-6 border border-[#D4A373]/30"
        >
          <p className="text-sm text-[#78716C] mb-2">We haven't forgotten about you.</p>
          <h3 className="text-xl font-bold text-[#292524] mb-3">
            21 Days to Calm & Connection
          </h3>
          <p className="text-sm text-[#57534E] mb-4">
            A gentle 21-day journey designed specifically for parents. 
            Move from stress and overwhelm to calm presence and deeper connection.
          </p>
          <div className="space-y-2">
            {["Daily calming practices", "Simple connection exercises", "Gentle reflections"].map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-[#4A6741]">
                <div className="w-1.5 h-1.5 rounded-full bg-[#D4A373]" />
                <span>{item}</span>
              </div>
            ))}
          </div>
        </motion.div>
        
        {/* Registration Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-2xl shadow-lg p-6"
        >
          <h3 className="font-semibold text-[#292524] mb-4">Create Your Account</h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-[#57534E] mb-1 block">Your Name</label>
              <Input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your name"
                required
                data-testid="parent-name-input"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-[#57534E] mb-1 block">Email</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                data-testid="parent-email-input"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-[#57534E] mb-1 block">Password</label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Create a password"
                required
                minLength={6}
                data-testid="parent-password-input"
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-[#57534E] mb-1 block">Confirm Password</label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                required
                data-testid="parent-confirm-password-input"
              />
            </div>
            
            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#4A6741] hover:bg-[#3D5A35] text-white py-3"
              data-testid="parent-submit-btn"
            >
              {submitting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  Verify & Begin Your Journey
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </form>
          
          <p className="text-xs text-center text-[#A8A29E] mt-4">
            By creating an account, you verify your teen's access to Reclaim's Teen Zone
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default ParentVerificationPage;
